import type { RequirementEvaluation } from '@milepost/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { StatusGlyph } from '../components/StatusGlyph';
import { UploadDialog } from '../components/UploadDialog';
import { api, type CatalogEntry, type DriverDetail as Detail } from '../lib/api';
import {
  DRIVER_CLASS_LABEL,
  formatDate,
  formatDue,
  fullName,
  STATUS_RANK,
} from '../lib/format';
import './DriverDetail.css';

export function DriverDetail() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const [uploadFor, setUploadFor] = useState<RequirementEvaluation | null>(null);

  const { data, isPending, error } = useQuery({
    queryKey: ['driver', id],
    queryFn: () => api.get<Detail>(`/api/drivers/${id}`),
  });

  const { data: catalog } = useQuery({
    queryKey: ['requirements'],
    queryFn: () => api.get<{ requirements: CatalogEntry[] }>('/api/drivers/requirements'),
    staleTime: Infinity,
  });

  const catalogByCode = useMemo(
    () => new Map((catalog?.requirements ?? []).map((r) => [r.code, r])),
    [catalog],
  );

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['driver', id] });
    void queryClient.invalidateQueries({ queryKey: ['roster'] });
    void queryClient.invalidateQueries({ queryKey: ['review-queue'] });
  };

  if (isPending) return <p className="muted">Loading…</p>;
  if (error) return <p className="field-error">{(error as Error).message}</p>;
  if (!data) return null;

  const { driver, compliance, artifacts } = data;

  // Grouped by what needs doing, not alphabetically. The grouping is the information.
  const actionable = compliance.requirements
    .filter((r) => ['expired', 'missing', 'pending_review', 'expiring_soon'].includes(r.status))
    .sort((a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status]);
  const settled = compliance.requirements.filter((r) => r.status === 'satisfied');
  const notApplicable = compliance.requirements.filter((r) => r.status === 'not_applicable');

  return (
    <div className="dd">
      <nav className="breadcrumb">
        <Link to="/">Fleet</Link>
        <span aria-hidden="true">/</span>
        <span>{fullName(driver)}</span>
      </nav>

      <header className="dd-head">
        <div>
          <h1 className="dd-name">{fullName(driver)}</h1>
          <p className="dd-facts">
            <span className="class-tag">{DRIVER_CLASS_LABEL[driver.driverClass]}</span>
            <span className="muted">{driver.isInterstate ? 'Interstate' : 'Intrastate'}</span>
            {driver.employeeId && <span className="cite">ID {driver.employeeId}</span>}
            <span className="muted">
              {driver.status === 'terminated'
                ? `Left ${formatDate(driver.terminationDate)}`
                : `Hired ${formatDate(driver.hireDate)}`}
            </span>
          </p>
        </div>

        <div className={`dd-verdict ${compliance.compliant ? 'is-ok' : 'is-bad'}`}>
          <span className="dd-verdict-label">
            {compliance.compliant ? 'File is complete' : 'File is deficient'}
          </span>
          <span className="dd-verdict-detail tnum">
            {compliance.counts.satisfied} of{' '}
            {compliance.requirements.length - compliance.counts.notApplicable} items on file
          </span>
        </div>
      </header>

      {driver.hasMedicalExemption && (
        <p className="dd-note">
          This driver operates under a federal medical exemption, so the ordinary medical
          certification items do not apply. <span className="cite">49 CFR part 381</span>
        </p>
      )}

      <section className="card">
        <div className="card-header">
          <h2 className="dd-section-title">
            Needs attention <span className="muted tnum">({actionable.length})</span>
          </h2>
          {actionable.length === 0 && <StatusGlyph status="satisfied" />}
        </div>

        {actionable.length === 0 ? (
          <p className="dd-empty muted">Nothing outstanding. Every applicable item is on file.</p>
        ) : (
          <ul className="checklist">
            {actionable.map((r) => (
              <ChecklistRow
                key={r.requirementCode}
                requirement={r}
                catalog={catalogByCode.get(r.requirementCode)}
                artifacts={artifacts}
                onUpload={() => setUploadFor(r)}
                onReviewed={invalidate}
              />
            ))}
          </ul>
        )}
      </section>

      {settled.length > 0 && (
        <details className="card dd-collapsible">
          <summary>
            <span className="dd-section-title">On file</span>
            <span className="muted tnum">({settled.length})</span>
          </summary>
          <ul className="checklist">
            {settled.map((r) => (
              <ChecklistRow
                key={r.requirementCode}
                requirement={r}
                catalog={catalogByCode.get(r.requirementCode)}
                artifacts={artifacts}
                onUpload={() => setUploadFor(r)}
                onReviewed={invalidate}
              />
            ))}
          </ul>
        </details>
      )}

      {notApplicable.length > 0 && (
        <details className="card dd-collapsible">
          <summary>
            <span className="dd-section-title">Does not apply to this driver</span>
            <span className="muted tnum">({notApplicable.length})</span>
          </summary>
          <ul className="checklist is-quiet">
            {notApplicable.map((r) => (
              <li key={r.requirementCode} className="check-row">
                <span className="check-glyph">
                  <StatusGlyph status={r.status} variant="glyph" />
                </span>
                <div className="check-body">
                  <p className="check-title">{r.title}</p>
                  <p className="check-why muted">{r.explanation}</p>
                </div>
                <span className="cite check-cite">{r.cfrCite}</span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {uploadFor && (
        <UploadDialog
          driverId={driver.id}
          requirement={uploadFor}
          catalogEntry={catalogByCode.get(uploadFor.requirementCode)}
          onClose={() => setUploadFor(null)}
          onDone={() => {
            setUploadFor(null);
            invalidate();
          }}
        />
      )}
    </div>
  );
}

// ── One checklist row ───────────────────────────────────────────────────────────

interface RowProps {
  requirement: RequirementEvaluation;
  catalog: CatalogEntry | undefined;
  artifacts: Detail['artifacts'];
  onUpload: () => void;
  onReviewed: () => void;
}

function ChecklistRow({ requirement: r, catalog, artifacts, onUpload, onReviewed }: RowProps) {
  const [showWhy, setShowWhy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');

  const pending = artifacts.find(
    (a) => a.requirement_code === r.requirementCode && a.review_state === 'pending_review',
  );
  const onFile = artifacts.find((a) => a.id === r.satisfiedBy);

  const review = useMutation({
    mutationFn: (body: { decision: 'accept' | 'reject'; reason?: string }) =>
      api.post(`/api/drivers/artifacts/${pending!.id}/review`, body),
    onSuccess: onReviewed,
  });

  return (
    <li className={`check-row status-${r.status}`}>
      <span className="check-glyph">
        <StatusGlyph status={r.status} variant="glyph" />
      </span>

      <div className="check-body">
        <p className="check-title">
          {r.title}
          {catalog?.disqualifying && (
            <span className="check-flag" title="A lapse means the driver is not qualified to drive">
              disqualifying
            </span>
          )}
          {r.sensitivity === 'restricted' && (
            <span className="check-flag is-restricted" title="Access is limited and logged">
              restricted
            </span>
          )}
        </p>

        <p className="check-why">{r.explanation}</p>

        <div className="check-meta">
          {r.effectiveAt && <span className="tnum">Issued {formatDate(r.effectiveAt)}</span>}
          {r.expiresAt && <span className="tnum">Expires {formatDate(r.expiresAt)}</span>}
          {r.daysUntilDue !== null && r.status !== 'satisfied' && (
            <span className="tnum">{formatDue(r.daysUntilDue)}</span>
          )}
          {onFile?.has_file && (
            <a href={`/api/documents/${onFile.id}/download`}>Download</a>
          )}
          {catalog?.note && (
            <button type="button" className="check-why-btn" onClick={() => setShowWhy((v) => !v)}>
              {showWhy ? 'Hide the rule' : 'Why is this required?'}
            </button>
          )}
        </div>

        {showWhy && catalog?.note && (
          <p className="check-note prose">
            {catalog.note} <span className="cite">{r.cfrCite}</span>
          </p>
        )}

        {pending && (
          <div className="check-review">
            <p className="check-review-head">
              A submission is waiting for review. It does not count until you accept it.
            </p>
            <div className="row">
              {pending.has_file && (
                <a
                  className="btn btn-secondary btn-sm"
                  href={`/api/documents/${pending.id}/download`}
                >
                  Open the file
                </a>
              )}
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => review.mutate({ decision: 'accept' })}
                disabled={review.isPending}
              >
                Accept
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setRejecting((v) => !v)}
              >
                Reject
              </button>
            </div>

            {rejecting && (
              <div className="check-reject">
                <label htmlFor={`reason-${r.requirementCode}`}>
                  What does the driver need to send instead?
                </label>
                <textarea
                  id={`reason-${r.requirementCode}`}
                  rows={2}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="The expiry date is cut off — resend with the whole card in frame."
                />
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  disabled={reason.trim().length === 0 || review.isPending}
                  onClick={() => review.mutate({ decision: 'reject', reason })}
                >
                  Send back to driver
                </button>
              </div>
            )}

            {review.error && <p className="field-error">{(review.error as Error).message}</p>}
          </div>
        )}
      </div>

      <div className="check-actions">
        <span className="cite check-cite">{r.cfrCite}</span>
        {r.artifactKind === 'document' && !pending && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={onUpload}>
            {r.status === 'satisfied' ? 'Replace' : 'Upload'}
          </button>
        )}
      </div>
    </li>
  );
}
